import { useState } from "react";
import { reportLostDevice, type DeviceReportInput } from "../../lib/guide-api";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { AlertIcon, CheckIcon, CloseIcon, ShieldIcon } from "../../components/ui/icons";

// G-03-M01 端末紛失申告モーダル（F-GUIDE-032）。ログイン画面（サインイン未完了）から開くため
// 公開ルート POST /guide-device-reports を叩く。バックエンドは email 突合で本人を特定し、
// 一致すれば該当ガイドの全セッションを即時失効する（guide-sessions.ts）。
// 列挙防止のためフロントも成否に関わらず同一の受理表示にする。

const ISSUE_TYPES: Array<{ value: DeviceReportInput["issueType"]; label: string }> = [
  { value: "lost_device", label: "Lost device" },
  { value: "compromised_account", label: "Compromised account" },
  { value: "other", label: "Other" },
];

export function LostDeviceModal({
  email: initialEmail,
  onClose,
}: {
  email: string;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [issueType, setIssueType] = useState<DeviceReportInput["issueType"]>("lost_device");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await reportLostDevice({
        email: email.trim().toLowerCase(),
        issueType,
        details: details.trim() || undefined,
      });
      setSubmitted(true);
    } catch {
      // 列挙防止のためエラー文言も一般化する（メール不一致等の詳細は出さない）。
      setError("We couldn't submit the report right now. Please try again or email support.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Lost device report"
    >
      <div className="relative w-full max-w-md rounded-t-2xl bg-paper p-5 shadow-card sm:rounded-2xl">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-4 top-4 grid size-8 place-items-center rounded-full text-muted hover:bg-canvas"
        >
          <CloseIcon className="size-5" />
        </button>

        {submitted ? (
          <div className="py-2">
            <div className="grid size-11 place-items-center rounded-full bg-success/10 text-success">
              <CheckIcon className="size-6" />
            </div>
            <h2 className="mt-3 text-lg font-bold text-ink">Report received</h2>
            <p className="mt-1.5 text-sm text-muted">
              Thank you. TST support will follow up by email. If this affects your account, you may
              need to sign in again with a new verification code.
            </p>
            <Button size="block" className="mt-5" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : (
          <div>
            <div className="flex items-start gap-3">
              <div className="grid size-11 shrink-0 place-items-center rounded-full bg-accent/10 text-accent">
                <ShieldIcon className="size-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-ink">Lost device report</h2>
                <p className="mt-1 text-sm text-muted">
                  Tell TST support if your guide device or email access may be compromised.
                </p>
              </div>
            </div>

            <label htmlFor="ldr-email" className="mt-4 block text-sm font-medium text-ink-soft">
              Registered email
              <input
                id="ldr-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="guide@example.com"
                className="mt-1 h-11 w-full rounded-lg border border-line bg-paper px-3 text-sm text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              />
            </label>

            <label htmlFor="ldr-issue" className="mt-3 block text-sm font-medium text-ink-soft">
              Issue type
              <select
                id="ldr-issue"
                value={issueType}
                onChange={(e) => setIssueType(e.target.value as DeviceReportInput["issueType"])}
                className="mt-1 h-11 w-full rounded-lg border border-line bg-paper px-3 text-sm text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              >
                {ISSUE_TYPES.map((it) => (
                  <option key={it.value} value={it.value}>
                    {it.label}
                  </option>
                ))}
              </select>
            </label>

            <label htmlFor="ldr-details" className="mt-3 block text-sm font-medium text-ink-soft">
              Details
              <textarea
                id="ldr-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                placeholder="Add timing, device model, and contact method."
                className="mt-1 w-full resize-none rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink placeholder:text-faint outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              />
            </label>

            <div className="mt-4 flex items-start gap-2 rounded-lg bg-warning/10 p-3 text-sm text-warning">
              <AlertIcon className="mt-0.5 size-4 shrink-0" />
              <span>We may temporarily lock guide access while we verify the report.</span>
            </div>

            {error && <p className="mt-3 text-sm text-danger">{error}</p>}

            <div className="mt-4 flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="block"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="block"
                onClick={() => void submit()}
                disabled={busy || !email.trim()}
              >
                {busy ? <Spinner className="text-white" /> : "Submit report"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
